"use client";

import { useLayoutEffect } from "react";

import "@calcom/embed-core/src/embed-iframe";
import { brandLegal } from "@calcom/features/teams/lib/brandLegal";
import { brandBySlug, BRAND_SKINS } from "@calcom/features/teams/lib/brandSkins";
import { HttpError } from "@calcom/lib/http-error";
import { Button } from "@calcom/ui/components/button";

type Props = {
  statusCode?: number | null;
  error?: Error | HttpError | null;
  message?: string;
  /** Display debugging information */
  displayDebug?: boolean;
  children?: never;
  reset?: () => void;
};

const defaultProps = {
  displayDebug: false,
};

const ErrorDebugPanel: React.FC<{ error: Props["error"]; children?: never }> = (props) => {
  const { error: e } = props;

  const debugMap = [
    ["error.message", e?.message],
    ["error.name", e?.name],
    ["error.class", e instanceof Error ? e.constructor.name : undefined],
    ["http.url", e instanceof HttpError ? e.url : undefined],
    ["http.status", e instanceof HttpError ? e.statusCode : undefined],
    ["http.cause", e instanceof HttpError ? e.cause?.message : undefined],
    ["error.stack", e instanceof Error ? e.stack : undefined],
  ];

  return (
    <div className="bg-default overflow-hidden shadow sm:rounded-lg">
      <div className="border-subtle border-t px-4 py-5 sm:p-0">
        <dl className="sm:divide-subtle sm:divide-y">
          {debugMap.map(([key, value]) => {
            if (value !== undefined) {
              return (
                <div key={key} className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                  <dt className="text-emphasis text-sm font-bold">{key}</dt>
                  <dd className="text-emphasis mt-1 text-sm sm:col-span-2 sm:mt-0">{value}</dd>
                </div>
              );
            }
          })}
        </dl>
      </div>
    </div>
  );
};

export const ErrorPage: React.FC<Props> = (props) => {
  const { message, statusCode, error, displayDebug } = { ...defaultProps, ...props };
  const handleReset = () => {
    window.location.reload();
    props.reset?.();
  };

  // useLayoutEffect runs synchronously before browser paint, ensuring it's set early
  useLayoutEffect(() => {
    if (statusCode && typeof window !== "undefined") {
      window.CalComPageStatus = statusCode.toString();
    }
  }, [statusCode]);

  // Marque posée sur <html data-rdv-brand> par le layout racine ; sans marque : Café Crème Club.
  const brandSlug = typeof document !== "undefined" ? document.documentElement.dataset.rdvBrand : undefined;
  const skin = brandBySlug(brandSlug) ?? BRAND_SKINS["cafe-creme-club"];
  const legal = brandLegal(skin);
  const contactHref = legal.contactEmail ? `mailto:${legal.contactEmail}` : legal.contactUrl;

  return (
    <>
      <div className="flex min-h-screen bg-[#F4F4F5] px-4 py-10" data-testid="brand-error-page">
        <div className="m-auto w-full max-w-xl rounded-2xl bg-white px-6 py-10 text-center shadow-sm sm:px-10">
          <img src={skin.markUrl} alt={skin.name} width={56} height={56} className="mx-auto h-14 w-14" />
          {statusCode ? (
            <p className="mt-6 text-sm font-semibold uppercase tracking-wide text-[#6B7280]">
              Erreur {statusCode}
            </p>
          ) : null}
          <h1 className="mt-2 text-2xl font-semibold text-[#1C1A17]">Un problème est survenu de notre côté.</h1>
          <p className="mx-auto mt-3 max-w-md text-base text-[#4B5563]">
            Réessayez dans un instant. Si le problème continue, écrivez-nous en recopiant le message
            ci-dessous : il nous aidera à corriger vite.
          </p>
          {message ? (
            <pre className="mx-auto mt-6 w-full max-w-md whitespace-normal wrap-break-word rounded-md bg-[#F4F4F5] p-4 text-left text-sm text-[#1C1A17]">
              {message}
            </pre>
          ) : null}
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button onClick={handleReset}>Réessayer</Button>
            <Button color="secondary" href={contactHref}>
              Contacter {skin.name}
            </Button>
          </div>
        </div>
      </div>
      {displayDebug && (
        <div className="flex-wrap">
          <ErrorDebugPanel error={error} />
        </div>
      )}
    </>
  );
};
